import { EditorDefns, TlfViewModeConfig } from "../../utility/interface";
import {
  type ArsListingColumn,
  type ArsListingRow,
  EMPTY_HEADER_COL_ID,
  HEADER_ROW_ID,
} from "./interface";

/**
 * Builds scaffold cell values for selected listing columns.
 *
 * @param selectedColumns - Selected listing columns
 * @param arsBodyRows - Body row definitions controlling height and display mode
 * @param editorDefns - Editor definitions for converting text to T and vice versa
 * @param tlfViewMode - TLF view mode controlling how cell data is displayed
 * @returns Row/column cell value map for scaffold rendering
 */
export const buildScaffoldCellData = <T>(
  selectedColumns: ArsListingColumn<T>[],
  arsBodyRows: ArsListingRow[],
  editorDefns: EditorDefns<T>,
  tlfViewMode: TlfViewModeConfig,
): Record<string, Record<string, T>> => {
  if (selectedColumns.length === 0) {
    const emptyBodyRows = arsBodyRows.reduce<Record<string, Record<string, T>>>((acc, row) => {
      acc[row.id] = { [EMPTY_HEADER_COL_ID]: editorDefns.textToT("") };
      return acc;
    }, {});
    return {
      [HEADER_ROW_ID]: {
        [EMPTY_HEADER_COL_ID]: editorDefns.textToT("Drop variables here"),
      },
      ...emptyBodyRows,
    };
  }

  const bodyRowEntries = arsBodyRows.reduce<Record<string, Record<string, T>>>((acc, row) => {
    acc[row.id] = {};
    return acc;
  }, {});
  const cellData: Record<string, Record<string, T>> = {
    [HEADER_ROW_ID]: {},
    ...bodyRowEntries,
  };

  selectedColumns.forEach((column) => {
    const isAnnotations = tlfViewMode?.value === "ANNOTATIONS";
    const label = isAnnotations
      ? editorDefns.textToT(column.variables.map((v) => `${v.dataset}.${v.variable}`).join("/"))
      : column.headerText;

    cellData[HEADER_ROW_ID][column.id] = label;
    arsBodyRows.forEach((bodyRow) => {
      cellData[bodyRow.id][column.id] = bodyRow.ellipsis
        ? editorDefns.textToT("...")
        : isAnnotations
          ? editorDefns.textToT(column.resultPattern)
          : Array.isArray(column.displayPattern)
            ? editorDefns.joinTintoBlock(column.displayPattern)
            : column.displayPattern;
    });
  });

  return cellData;
};
